import React, { useState, useEffect } from 'react';
import { Box, Text, HStack, VStack, Button } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import EditButton from '../components/EditButton';
import PhotoGallery from '../components/PhotoGallery';
import { useConfig } from '../contexts/ConfigContext';
import { useUser } from '../contexts/UserContext';
import { useSupabase } from '../contexts/SupabaseContext';
import { getCachedUserProfile, getCityLabel, getCountryLabel, isSubscriptionExpired } from '../utils';

const BusinessProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { config } = useConfig();
  const { user, logout: logoutUser } = useUser();
  const { logout: logoutSupabase } = useSupabase();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!config?.apiUrl || !user?.id) {
        return;
      }

      try {
        setLoading(true);
        const profileData = await getCachedUserProfile(config.apiUrl, user.id, user.userType);
        setUserData(profileData);
      } catch (err: any) {
        console.error('Error fetching profile:', err);
        setError(err.response?.data?.error || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [config?.apiUrl, user?.id, user?.userType]);

  const businessData = {
    name: userData?.data?.businessName || 'Unknown',
    contactDetails: {
      firstName: userData?.data?.contactPerson?.firstName || '',
      lastName: userData?.data?.contactPerson?.lastName || '',
      email: userData?.email || '',
      position: userData?.data?.contactPerson?.position || '',
      phoneNumber: userData?.data?.contactPerson?.phoneNumber || ''
    },
    businessDetails: {
      category: userData?.businessCategory || '',
      businessName: userData?.data?.businessName || 'Unknown',
      location: userData?.city && userData?.country
        ? `${getCityLabel(userData.city)}, ${getCountryLabel(userData.country)}`
        : userData?.country ? getCountryLabel(userData.country) : undefined,
      businessAddress: userData?.data?.businessAddress || '',
      website: userData?.data?.website || userData?.data?.social?.website || '',
      instagram: userData?.data?.social?.instagram || '',
      tiktok: userData?.data?.social?.tiktok || ''
    }
  };

  const photos = userData?.data?.photos?.length > 0
    ? userData.data.photos
    : [];

  const handleLogout = async () => {
    try {
      logoutUser();
      sessionStorage.removeItem('authReturnPath');
      await logoutSupabase();
      setShowLogoutDialog(false);
      navigate('/');
    } catch (error: any) {
      console.error('Logout error:', error);
      logoutUser();
      sessionStorage.removeItem('authReturnPath');
      setShowLogoutDialog(false);
      navigate('/');
    }
  };

  const handleCancelLogout = () => {
    setShowLogoutDialog(false);
  };

  const handleShowLogoutDialog = () => {
    setShowLogoutDialog(true);
  };

  const handleEditProfile = () => {
    navigate('/business-edit-profile');
  };

  return (
    <Box className="base-container">
      {/* Profile Background Image */}
      <PhotoGallery images={photos} />

      {/* Profile Header */}
      <Box className="profile-header">
        <Text className="title-text">
          My profile
        </Text>
        <EditButton
          onClick={handleEditProfile}
          borderColor="#FFFFFF"
          hoverBorderColor="#FFFFFF"
          hoverBackgroundColor="rgba(28, 28, 30, 0.9)"
          backgroundColor="rgba(28, 28, 30, 0.8)"
        />
      </Box>

      {/* Profile Name */}
      <Box
        position="absolute"
        top="320px"
        left="24px"
        zIndex={3}
      >
        <Text className="title-text-big">
          {businessData.name}
        </Text>
      </Box>

      {/* Divider */}
      <Box className="navigation-bar-divider" top="370px" />

      {/* Content */}
      <Box className="form-content-container">
        <VStack spacing="20px" align="start">
          {/* Contact Details Section */}
          {(businessData.contactDetails.firstName || businessData.contactDetails.lastName || businessData.contactDetails.email || businessData.contactDetails.position || businessData.contactDetails.phoneNumber) && (
            <Box
              width="100%"
              backgroundColor="#120F1B"
              borderRadius="28px"
              padding="18px"
            >
              <Text className='static-text-big' marginBottom="18px">
                Contact details
              </Text>

              <VStack spacing="18px" align="stretch">
                <Text className='static-text-big-white'>
                  First Name - {businessData.contactDetails.firstName}
                </Text>
                <Text className='static-text-big-white'>
                  Last Name - {businessData.contactDetails.lastName}
                </Text>
                <Text className='static-text-big-white'>
                  Email - {businessData.contactDetails.email}
                </Text>
                <Text className='static-text-big-white'>
                  Position - {businessData.contactDetails.position}
                </Text>
                <Text className='static-text-big-white'>
                  Phone number - {businessData.contactDetails.phoneNumber}
                </Text>
              </VStack>
            </Box>
          )}

          {/* Business Details Section */}
          {(businessData.businessDetails.category || businessData.businessDetails.businessName || businessData.businessDetails.location || businessData.businessDetails.businessAddress || businessData.businessDetails.website || businessData.businessDetails.instagram || businessData.businessDetails.tiktok) && (
            <Box
              width="100%"
              backgroundColor="#120F1B"
              borderRadius="28px"
              padding="18px"
            >
              <Text className='static-text-big' marginBottom="18px">
                Business details
              </Text>

              <VStack spacing="18px" align="stretch">
                <Text className='static-text-big-white'>
                  Business category - {businessData.businessDetails.category}
                </Text>
                <Text className='static-text-big-white'>
                  Business name - {businessData.businessDetails.businessName}
                </Text>
                <Text className='static-text-big-white'>
                  Location - {businessData.businessDetails.location}
                </Text>
                <Text className='static-text-big-white'>
                  Business address - {businessData.businessDetails.businessAddress}
                </Text>
                <Text className='static-text-big-white'>
                  Website - {businessData.businessDetails.website}
                </Text>
                <Text className='static-text-big-white'>
                  Instagram - {businessData.businessDetails.instagram}
                </Text>
                <Text className='static-text-big-white'>
                  Tiktok - {businessData.businessDetails.tiktok}
                </Text>
              </VStack>
            </Box>
          )}

          {/* Subscription Status */}
          <Box marginTop="16px">
            <HStack spacing="8px">
              <Box
                width="8px"
                height="8px"
                borderRadius="50%"
                backgroundColor={isSubscriptionExpired(userData?.data?.paidTillDate) ? "#595C5D" : "#C6D959"}
              />
              {isSubscriptionExpired(userData?.data?.paidTillDate) ? (
                <Text className="subtitle-text" color="#595C5D">
                  Subscription expired
                </Text>
              ) : userData?.data?.paidTillDate ? (
                <Text className="subtitle-text" color="#C6D959">
                  Subscription valid until {new Date(userData.data.paidTillDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </Text>
              ) : (
                <Text className="subtitle-text" color="#595C5D">
                  No active subscription
                </Text>
              )}
            </HStack>
          </Box>

          {/* Logout Section */}
          <Box marginTop="20px">
            <HStack spacing="6px" alignItems="center" cursor="pointer" onClick={handleShowLogoutDialog}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 0C6.55 0 7 0.45 7 1V3H14C14.55 3 15 3.45 15 4V12C15 12.55 14.55 13 14 13H7V15C7 15.55 6.55 16 6 16H1C0.45 16 0 15.55 0 15V1C0 0.45 0.45 0 1 0H6ZM8 5V11H13V5H8ZM2 2V14H5V2H2Z"
                  fill="#595C5D"
                />
              </svg>
              <Text className='regular-text' color="#595C5D">
                Log out
              </Text>
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* Logout Dialog */}
      {showLogoutDialog && (
        <>
          <Box className="modal-backdrop" zIndex={10} onClick={handleCancelLogout} />

          <Box className="bottom-sheet-dialog" height="360px" zIndex={11} onClick={(e) => e.stopPropagation()}>
            <Box
              className="bottom-sheet-bg"
              style={{ backgroundImage: "url('/assets/images/confirmation-sheet-bg.svg')"}}
            />

            <Box
              className="bottom-sheet-indicator"
              style={{ backgroundImage: "url('/assets/images/bottom-sheet-indicator.svg')"}}
            />

            <VStack className="bottom-sheet-content">
              <Text className="dialog-title">
                Are you sure?
              </Text>

              <Text className="dialog-subtitle">
                you want to log out from the app
              </Text>

              <Button className="button-cancel" onClick={handleCancelLogout}>
                Cancel
              </Button>

              <Button className="button-confirm" onClick={handleLogout}>
                Log out
              </Button>
            </VStack>

            <Box
              className="bottom-sheet-footer"
              style={{ backgroundImage: "url('/assets/images/home-indicator-confirmation.svg')"}}
            />
          </Box>
        </>
      )}

      {/* Bottom Navigation */}
      {!showLogoutDialog && (
        <Box className="bottom-navigation-bar-box">
          <Button
            className="bottom-navigation-bar-button"
            onClick={() => navigate('/business-home')}
          >
            <svg width="18" height="20" viewBox="0 0 18 20" fill="none">
              <path
                d="M9 0L0 7V20H7V12H11V20H18V7L9 0Z"
                fill="#90909A"
                stroke="none"
                strokeWidth="1.5"
              />
            </svg>
          </Button>

          <Button
            className="bottom-navigation-bar-button"
            onClick={() => navigate('/business-event-creation')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 5V19M5 12H19"
                stroke="#90909A"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>

          <Button
            className="bottom-navigation-bar-button"
            onClick={() => navigate('/business-profile')}
          >
            <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
              <path
                d="M8 0C5.79 0 4 1.79 4 4C4 6.21 5.79 8 8 8C10.21 8 12 6.21 12 4C12 1.79 10.21 0 8 0ZM8 10C5.33 10 0 11.34 0 14V16C0 17.1 0.9 18 2 18H14C15.1 18 16 17.1 16 16V14C16 11.34 10.67 10 8 10Z"
                fill="none"
                stroke="#C6D959"
                strokeWidth="1.5"
              />
            </svg>
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BusinessProfilePage;
